import React, { useState } from 'react';
import { Form, Input, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from '.';
import { useTimer } from '../hooks/useTimer';
import type { RootState } from '../store';
import { useDeactivateProfile, useSendOtp } from '../service';
import { logout } from '../store/userSlice';

interface DeactivateModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface OtpViewProps {
    countryCode: string;
    mobileNumber: string;
    timer: number;
    isTimerRunning: boolean;
    onResendOtp: () => void;
    onVerifyOtp: (otp: string) => void;
}

const OtpView: React.FC<OtpViewProps> = ({
    countryCode,
    mobileNumber,
    timer,
    isTimerRunning,
    onResendOtp,
    onVerifyOtp
}) => {
    const [otp, setOtp] = useState('');

    return (
        <>
            <p className="text-sm text-textColor font-outfit text-center mb-4">
                We have sent an OTP to your registered mobile number to deactivate your account.
            </p>

            <p className="text-sm text-gray-500 font-outfit text-center mb-4">
                Enter OTP sent to {countryCode} {mobileNumber}
            </p>

            <Form.Item className="flex justify-center mb-6">
                <Input.OTP
                    length={6}
                    className="gap-2"
                    onChange={setOtp}
                />
            </Form.Item>

            <div className="flex flex-row gap-1 justify-center mb-4">
                <span className="text-sm text-textLight font-outfit">
                    Didn&apos;t receive the OTP?
                </span>
                <button
                    onClick={onResendOtp}
                    disabled={isTimerRunning}
                    className={`text-sm ${!isTimerRunning ? 'text-goldenDark hover:text-primary' : 'text-grayColor'} transition-colors duration-150 font-outfit`}
                >
                    Resend
                </button>
            </div>

            <Form.Item className="flex justify-center mb-4">
                <Button
                    name="VERIFY"
                    type="first"
                    onClick={() => onVerifyOtp(otp)}
                />
            </Form.Item>

            {timer > 0 && (
                <div className="flex flex-row justify-center text-sm text-textLight font-outfit">
                    00 : {String(timer).padStart(2, '0')} s
                </div>
            )}
        </>
    );
};

const DeactivateModal: React.FC<DeactivateModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const [form] = Form.useForm();

    const sendOtpMutation = useSendOtp();
    const { mutate: deactivateProfile } = useDeactivateProfile();

    const [step, setStep] = useState<'confirm' | 'otp'>('confirm');

    const countryCode = useSelector((state: RootState) => state.user.country_code)
    const mobileNumber = useSelector((state: RootState) => state.user.mobile_Number)

    const {
        seconds: timer,
        isRunning: isTimerRunning,
        start: startOtpTimer,
        reset: resetOtpTimer,
    } = useTimer({ initialSeconds: 59 });

    if (!isOpen) return null;

    const closeHandler = () => {
        onClose();
        form.resetFields();
        resetOtpTimer();
        setStep('confirm');
    };

    const verifyOtpHandler = (otp: string) => {
        deactivateProfile(otp, {
            onSuccess() {
                dispatch(logout())
                closeHandler();
                navigate('/deactivate', {
                    state: { from: '/' }
                });
            },
            onError() {
                closeHandler();
            },
        })
    };

    const handleDeleteConfirm = () => {
        sendOtpMutation.mutate({
            mobile_number: mobileNumber as string,
            country_code: countryCode as string,
        }, {
            onSuccess: () => {
                message.info("Enter otp recieved!")

                resetOtpTimer()
                startOtpTimer();
            },
            onError: (error) => {
                console.error('Failed to send OTP:', error);
                if (error instanceof Error) {
                    message.error(error.message);
                } else {
                    message.error('An unexpected error occurred. Please try again.');

                }

            },
        });
        setStep('otp');
        startOtpTimer();
    };

    const resendOtpHandler = () => {
        resetOtpTimer();
        startOtpTimer();
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
            onClick={closeHandler}
        >
            <div
                className="bg-secondary rounded-xl w-4/5 md:w-2/4 lg:w-2/6 py-4 z-[100]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-3 lg:gap-4 mx-6 lg:mx-10 my-8">
                    <h1 className="text-center text-2xl font-medium mb-3">
                        {step === 'confirm' ? 'CONFIRM DEACTIVATION' : 'VERIFY OTP'}
                    </h1>

                    <Form form={form} className="w-full" layout="vertical">
                        {step === 'confirm' ? (
                            <>
                                <p className="text-md text-textColor font-outfit text-center mb-4">
                                    You will lose all the data once you deactivate your account.
                                    <br />
                                    Are you sure you want to deactivate?
                                </p>
                                <div className="flex gap-3 justify-center my-6">
                                    <Button
                                        name="Yes, Delete"
                                        type="first"
                                        htmlType="button"
                                        onClick={handleDeleteConfirm}
                                    />
                                    <Button
                                        name="Cancel"
                                        type="second"
                                        htmlType="button"
                                        onClick={closeHandler}
                                    />
                                </div>
                            </>
                        ) : (
                            <OtpView
                                countryCode={countryCode as string}
                                mobileNumber={mobileNumber as string}
                                timer={timer}
                                isTimerRunning={isTimerRunning}
                                onResendOtp={resendOtpHandler}
                                onVerifyOtp={verifyOtpHandler}
                            />
                        )}
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default DeactivateModal;
